import { useState } from 'react';

import { Button } from '@/components/Button/Button';
import { Callout } from '@/components/Callout/Callout';
import { Dialog } from '@/components/Dialog/Dialog';
import { RolePicker } from '@/components/RolePicker/RolePicker';
import type { PlatformRole } from '@/graphql/staff';

import styles from './RolesForm.module.css';

export interface RolesFormProps {
  /** Who is being changed, as the page names them. */
  person: string;
  current: readonly PlatformRole[];
  busy?: boolean;
  error?: string | null;
  onSubmit: (roles: PlatformRole[]) => void;
  onCancel: () => void;
}

/** Change which roles someone holds. Saved as one assignment per change. */
export function RolesForm({
  person,
  current,
  busy = false,
  error = null,
  onSubmit,
  onCancel,
}: RolesFormProps) {
  const [roles, setRoles] = useState<PlatformRole[]>([...current]);
  const unchanged =
    roles.length === current.length && roles.every((role) => current.includes(role));

  return (
    <Dialog
      actions={
        <>
          <Button disabled={busy} onClick={onCancel} variant="ghost">
            Cancel
          </Button>
          <Button disabled={unchanged} loading={busy} onClick={() => onSubmit(roles)}>
            Save roles
          </Button>
        </>
      }
      description="Changes apply the next time they load a page."
      dismissable={!busy}
      onClose={onCancel}
      title={`Roles for ${person}`}
    >
      <div className={styles.form}>
        <RolePicker disabled={busy} onChange={setRoles} value={roles} />
        {roles.length === 0 ? (
          <Callout title="No roles">
            They stay admitted but can't change anything. To take away their access entirely, remove
            them instead.
          </Callout>
        ) : null}
        {error ? (
          <Callout tone="danger" title="That didn't work">
            {error}
          </Callout>
        ) : null}
      </div>
    </Dialog>
  );
}
